/**
 * Background token keep-alive for the MCP server.
 *
 * Public-client refresh tokens die after ~90 days WITHOUT USE — but every
 * silent redemption rotates the token and resets that window. Tool calls
 * already redeem silently, so an actively-used server never expires; this
 * timer covers the long-lived-but-idle session (an AI client left open for
 * days) and surfaces refresh failures in the log when they happen instead of
 * at the user's next command.
 *
 * Deliberately NOT a fix for Conditional Access sign-in frequency: Entra
 * rejects silent redemption once the policy interval passes no matter how
 * fresh the refresh token is — only interactive sign-in satisfies it.
 *
 * The default interval (4h) is intentionally longer than an access token's
 * lifetime (~60–90 min), so every tick finds the cached access token expired
 * and actually redeems the refresh token rather than reading the cache.
 */

import { env } from "node:process";
import type { AuthProvider } from "./types.js";
import { logger } from "../utils/logger.js";

const DEFAULT_INTERVAL_HOURS = 4;

export interface KeepaliveHandle {
  stop(): void;
}

export type KeepaliveTickResult = "refreshed" | "signed-out" | "failed";

/**
 * Interval in ms from PA_MCP_TOKEN_KEEPALIVE_HOURS (default 4, 0 disables).
 * Anything unparseable or negative falls back to the default.
 */
export function getKeepaliveIntervalMs(): number {
  const raw = env["PA_MCP_TOKEN_KEEPALIVE_HOURS"];
  const hours = raw === undefined || raw.trim() === "" ? DEFAULT_INTERVAL_HOURS : Number(raw);
  if (!Number.isFinite(hours) || hours < 0) {
    return DEFAULT_INTERVAL_HOURS * 3_600_000;
  }
  return hours * 3_600_000;
}

/**
 * One keep-alive pass. Never throws.
 *
 * A signed-out server is a normal state (first run, or the user hasn't run
 * sign_in yet) — skipped quietly at debug. A signed-in account whose silent
 * refresh fails is the interesting signal and logs a warning with the
 * recovery path.
 */
export async function runKeepaliveTick(
  authProvider: AuthProvider,
  phase: "startup" | "interval"
): Promise<KeepaliveTickResult> {
  try {
    if (!(await authProvider.hasAccount())) {
      logger.debug({ phase }, "Token keep-alive: no signed-in account — skipping");
      return "signed-out";
    }
  } catch {
    // Cache unreadable — let getToken() produce the authoritative outcome.
  }

  try {
    await authProvider.getToken();
    logger.debug({ phase }, "Token keep-alive: silent refresh ok");
    return "refreshed";
  } catch (err) {
    logger.warn(
      { phase, err: err instanceof Error ? err.message : String(err) },
      "Token keep-alive: silent refresh failed — interactive sign-in will be required " +
        "(run the sign_in tool, or 'powerautomate-mcp --login' from a terminal)"
    );
    return "failed";
  }
}

/**
 * Start the keep-alive: one immediate warm-up pass, then a repeating timer.
 * The timer is unref'd so it never holds the process open. getToken() is
 * mutex-guarded per scope set, so a tick racing a real tool call is safe.
 */
export function startTokenKeepalive(authProvider: AuthProvider): KeepaliveHandle {
  const intervalMs = getKeepaliveIntervalMs();
  if (intervalMs === 0) {
    logger.debug("Token keep-alive disabled (PA_MCP_TOKEN_KEEPALIVE_HOURS=0)");
    return { stop: () => undefined };
  }

  void runKeepaliveTick(authProvider, "startup");

  const timer = setInterval(() => {
    void runKeepaliveTick(authProvider, "interval");
  }, intervalMs);
  timer.unref?.();

  logger.debug({ intervalHours: intervalMs / 3_600_000 }, "Token keep-alive scheduled");
  return { stop: () => clearInterval(timer) };
}
